import { authStore } from "@/lib/authStore";
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Clock,
  Database,
  Activity,
  Loader2,
  Wrench,
  Tag,
  FileWarning,
  PlayCircle,
  ShieldOff,
  ShieldCheck,
  PieChart,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

function getAuthHeaders() {
  const token = authStore.getAccessToken();
  return { Authorization: 'Bearer ' + token, 'Content-Type': 'application/json' };
}

const STATUS_COLORS = {
  completed: 'text-green-600 bg-green-500/10 border-green-500/20',
  indexed: 'text-green-600 bg-green-500/10 border-green-500/20',
  failed: 'text-red-600 bg-red-500/10 border-red-500/20',
  error: 'text-red-600 bg-red-500/10 border-red-500/20',
  pending: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
  running: 'text-blue-600 bg-blue-500/10 border-blue-500/20',
  uploading: 'text-amber-600 bg-amber-500/10 border-amber-500/20',
  quarantined: 'text-purple-600 bg-purple-500/10 border-purple-500/20',
};

// Pretty labels for parser-error categories (kept here as source of truth so
// the UI can render translated strings without an extra round-trip).
const CATEGORY_LABELS = {
  worker_restart: 'Worker restart',
  password_protected: 'Password-protected',
  corrupt_pdf: 'Corrupt PDF',
  scanned_no_ocr: 'Scanned (needs OCR)',
  encoding_error: 'Encoding error',
  unsupported_format: 'Unsupported format',
  oversized: 'Oversized',
  blob_missing: 'Blob missing',
  network_timeout: 'Network timeout',
  xlsx_malformed: 'Malformed XLSX',
  pptx_malformed: 'Malformed PPTX',
  uncategorized: 'Uncategorized',
};

function StatusBadge({ value }) {
  const cls = STATUS_COLORS[value] || 'text-muted-foreground bg-muted border-border';
  return (
    <span
      className={'inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium border ' + cls}
      data-testid={'status-badge-' + (value || 'unknown')}
    >
      {value || 'unknown'}
    </span>
  );
}

function StatTile({ icon: Icon, label, value, sub, tone }) {
  const toneCls =
    tone === 'red'
      ? 'border-red-500/30 bg-red-500/5'
      : tone === 'amber'
      ? 'border-amber-500/30 bg-amber-500/5'
      : tone === 'green'
      ? 'border-green-500/30 bg-green-500/5'
      : 'border-border bg-card';
  return (
    <div className={'rounded-xl border p-4 ' + toneCls} data-testid={'tile-' + label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}>
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon className="h-4 w-4" />
        <span className="text-xs uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
    </div>
  );
}

export default function IndexingJobsTab() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('failed');
  const [busy, setBusy] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [quarantined, setQuarantined] = useState([]);
  const [showQuarantine, setShowQuarantine] = useState(false);
  // Current tenant context — resolved from pipeline-stats.orgs[0]. For tenant
  // admins this matches their own org. For super-admins this is the first org
  // returned (default), and we append `?as_tenant=<this>` to every action so
  // require_tenant() resolves without forcing the operator to log out and
  // switch accounts. Without this every action returns 403 for super-admins.
  const [currentOrg, setCurrentOrg] = useState(null);

  // Build a `?as_tenant=<org>` query suffix to append to admin endpoints.
  // Returns "" when no org is known yet (first paint), "?as_tenant=X" or
  // "&as_tenant=X" depending on whether the URL already has a query string.
  function asTenantSuffix(path) {
    if (!currentOrg) return '';
    const sep = path.includes('?') ? '&' : '?';
    return sep + 'as_tenant=' + encodeURIComponent(currentOrg);
  }

  const fetchAll = useCallback(async () => {
    try {
      // First pass uses no tenant override — pipeline-stats works for both
      // tenant admins (sees own org) and super-admins (sees all orgs). We
      // then pick orgs[0] as the "current tenant" and re-issue the four
      // tenant-scoped fetches with `?as_tenant=` so super-admins also get data.
      const s = await fetch(API_URL + '/admin/rag/pipeline-stats', { headers: getAuthHeaders() });
      let pickedOrg = currentOrg;
      if (s.ok) {
        const sd = await s.json();
        const org = (sd.orgs && sd.orgs[0]) || null;
        setStats(org);
        if (org && org.org_id) {
          pickedOrg = org.org_id;
          if (org.org_id !== currentOrg) setCurrentOrg(org.org_id);
        }
      }
      const tenantQS = pickedOrg ? '&as_tenant=' + encodeURIComponent(pickedOrg) : '';
      const tenantQSOnly = pickedOrg ? '?as_tenant=' + encodeURIComponent(pickedOrg) : '';
      const [j, a, q] = await Promise.all([
        fetch(
          API_URL +
            '/admin/rag/job-logs?limit=100' +
            (filter === 'all' ? '' : '&status=' + encodeURIComponent(filter)) +
            tenantQS,
          { headers: getAuthHeaders() }
        ),
        fetch(API_URL + '/admin/rag/parser-analytics' + tenantQSOnly, { headers: getAuthHeaders() }),
        fetch(API_URL + '/admin/rag/quarantine?limit=50' + tenantQS, { headers: getAuthHeaders() }),
      ]);
      if (j.ok) {
        const jd = await j.json();
        setRows(jd.rows || []);
      }
      if (a.ok) {
        setAnalytics(await a.json());
      }
      if (q.ok) {
        const qd = await q.json();
        setQuarantined(qd.items || []);
      }
    } catch (e) {
      toast.error('Failed to load indexing stats: ' + (e.message || e));
    } finally {
      setLoading(false);
    }
  }, [filter, currentOrg]);

  useEffect(() => {
    setLoading(true);
    fetchAll();
    const t = setInterval(fetchAll, 10000); // live refresh every 10s
    return () => clearInterval(t);
  }, [fetchAll]);

  async function callAction(name, path, method) {
    setBusy((b) => Object.assign({}, b, { [name]: true }));
    try {
      const r = await fetch(API_URL + path + asTenantSuffix(path), {
        method: method || 'POST',
        headers: getAuthHeaders(),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || ('HTTP ' + r.status));
      toast.success(name + ': ' + (body.status || 'ok'));
      await fetchAll();
    } catch (e) {
      toast.error(name + ' failed: ' + (e.message || e));
    } finally {
      setBusy((b) => Object.assign({}, b, { [name]: false }));
    }
  }

  async function retryDoc(docId) {
    setBusy((b) => Object.assign({}, b, { ['retry-' + docId]: true }));
    try {
      const path = '/rag/ingest/document/' + docId + '/retry';
      const r = await fetch(API_URL + path + asTenantSuffix(path), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || ('HTTP ' + r.status));
      toast.success('Retry queued for doc');
      await fetchAll();
    } catch (e) {
      toast.error('Retry failed: ' + (e.message || e));
    } finally {
      setBusy((b) => Object.assign({}, b, { ['retry-' + docId]: false }));
    }
  }

  async function quarantineDoc(docId, filename) {
    // eslint-disable-next-line no-alert
    const reason = window.prompt(
      'Quarantine "' + (filename || docId) + '"?\n\n' +
        'Reason (e.g. "Source PDF is corrupt — vendor must re-export"):',
      ''
    );
    if (reason === null) return; // user cancelled
    setBusy((b) => Object.assign({}, b, { ['quar-' + docId]: true }));
    try {
      const path = '/admin/rag/documents/' + docId + '/quarantine';
      const r = await fetch(API_URL + path + asTenantSuffix(path), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: reason || '' }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || ('HTTP ' + r.status));
      toast.success(
        body.already_quarantined
          ? 'Already quarantined (' + (body.category || '?') + ')'
          : 'Quarantined as ' + (body.category || '?') +
              ' — ' + body.chunks_deleted + ' chunks removed'
      );
      await fetchAll();
    } catch (e) {
      toast.error('Quarantine failed: ' + (e.message || e));
    } finally {
      setBusy((b) => Object.assign({}, b, { ['quar-' + docId]: false }));
    }
  }

  async function unquarantineDoc(docId) {
    setBusy((b) => Object.assign({}, b, { ['unquar-' + docId]: true }));
    try {
      const path = '/admin/rag/documents/' + docId + '/unquarantine';
      const r = await fetch(API_URL + path + asTenantSuffix(path), {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || ('HTTP ' + r.status));
      toast.success('Restored to ' + (body.status || 'prior status'));
      await fetchAll();
    } catch (e) {
      toast.error('Unquarantine failed: ' + (e.message || e));
    } finally {
      setBusy((b) => Object.assign({}, b, { ['unquar-' + docId]: false }));
    }
  }

  async function bulkQuarantine(category, count) {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      'Quarantine ALL ' + count + ' ' + (CATEGORY_LABELS[category] || category) +
        ' documents?\n\nTheir chunks will be removed from FAISS but blobs retained.\nYou can restore individual docs later from the quarantine drawer.'
    );
    if (!ok) return;
    setBusy((b) => Object.assign({}, b, { ['bulk-quar-' + category]: true }));
    try {
      const path = '/admin/rag/quarantine/bulk';
      const r = await fetch(API_URL + path + asTenantSuffix(path), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          category,
          reason: 'Bulk action: ' + (CATEGORY_LABELS[category] || category),
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || ('HTTP ' + r.status));
      toast.success(
        'Quarantined ' + body.quarantined + '/' + body.total_targeted +
          ' · removed ' + body.chunks_deleted + ' chunks' +
          (body.skipped > 0 ? ' · skipped ' + body.skipped : '')
      );
      await fetchAll();
    } catch (e) {
      toast.error('Bulk quarantine failed: ' + (e.message || e));
    } finally {
      setBusy((b) => Object.assign({}, b, { ['bulk-quar-' + category]: false }));
    }
  }

  async function bulkRetry(category) {
    // eslint-disable-next-line no-alert
    const ok = window.confirm(
      'Re-queue all ' + (CATEGORY_LABELS[category] || category) + ' docs for indexing?'
    );
    if (!ok) return;
    setBusy((b) => Object.assign({}, b, { ['bulk-retry-' + category]: true }));
    try {
      const path = '/admin/rag/retry/bulk';
      const r = await fetch(API_URL + path + asTenantSuffix(path), {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ category }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.detail || ('HTTP ' + r.status));
      toast.success(
        'Retried ' + body.retried + '/' + body.total_targeted +
          (body.skipped_blob_missing > 0 ? ' · skipped (blob gone) ' + body.skipped_blob_missing : '')
      );
      await fetchAll();
    } catch (e) {
      toast.error('Bulk retry failed: ' + (e.message || e));
    } finally {
      setBusy((b) => Object.assign({}, b, { ['bulk-retry-' + category]: false }));
    }
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="indexing-jobs-loading">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const docs = (stats && stats.documents) || { total: 0, by_status: {}, stuck_uploading_2h_plus: 0 };
  const chunks = (stats && stats.chunks) || { in_mongo: 0, indexed_in_faiss: 0, desync_gap: 0 };
  const jobs = (stats && stats.indexing_jobs) || {};
  const topPatterns = ((stats && stats.errors && stats.errors.top_patterns) || []);
  const lastRebuild = stats && stats.last_rebuild;

  return (
    <div className="space-y-6" data-testid="indexing-jobs-tab">
      {/* Header + actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold">Indexing Jobs</h2>
          <p className="text-sm text-muted-foreground">
            Live ingestion pipeline. Refreshes every 10 s.
            {stats && stats.org_id ? ' Tenant: ' + stats.org_id : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={busy['refresh']}
            onClick={() => callAction('refresh', '/admin/rag/integrity-check')}
            data-testid="btn-integrity-check"
          >
            {busy['refresh'] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Activity className="h-4 w-4" />}
            <span className="ml-2">Integrity check</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy['rebuild']}
            onClick={() => callAction('rebuild', '/admin/rag/rebuild-index')}
            data-testid="btn-rebuild-index"
          >
            {busy['rebuild'] ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            <span className="ml-2">Rebuild index</span>
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={busy['backfill']}
            onClick={() => callAction('backfill', '/admin/rag/backfill-metadata')}
            data-testid="btn-backfill-metadata"
          >
            {busy['backfill'] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Tag className="h-4 w-4" />}
            <span className="ml-2">Backfill metadata</span>
          </Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          icon={Database}
          label="Documents"
          value={docs.total}
          sub={
            (docs.by_status.indexed || 0) +
            ' indexed · ' +
            (docs.by_status.uploading || 0) +
            ' uploading'
          }
        />
        <StatTile
          icon={Database}
          label="Chunks in Mongo"
          value={chunks.in_mongo.toLocaleString()}
          sub="canonical source of truth"
        />
        <StatTile
          icon={Activity}
          label="Indexed in FAISS"
          value={chunks.indexed_in_faiss.toLocaleString()}
          sub={'gap: ' + chunks.desync_gap.toLocaleString()}
          tone={chunks.desync_gap > 0 ? 'amber' : 'green'}
        />
        <StatTile
          icon={AlertTriangle}
          label="Failed docs"
          value={docs.by_status.error || 0}
          sub={(jobs.failed || 0) + ' failed jobs · ' + (jobs.pending || 0) + ' pending'}
          tone={(docs.by_status.error || 0) > 0 ? 'red' : 'green'}
        />
      </div>

      {/* Last rebuild */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground">
        <Clock className="h-3.5 w-3.5" />
        {lastRebuild ? (
          <>
            Last rebuild: <strong className="text-foreground">{lastRebuild.trigger}</strong>
            {' · '}
            <span className={lastRebuild.status === 'ok' ? 'text-green-600' : 'text-red-600'}>
              {lastRebuild.status}
            </span>
            {' · '}
            {lastRebuild.duration_ms} ms
            {' · '}
            {lastRebuild.indexed_chunks}/{lastRebuild.chunks_in_mongo} chunks
            {lastRebuild.started_at ? ' · ' + new Date(lastRebuild.started_at).toLocaleString() : ''}
          </>
        ) : (
          <>No rebuild recorded for this tenant.</>
        )}
      </div>

      {/* Top error patterns */}
      {topPatterns.length > 0 && (
        <div className="rounded-xl border border-red-500/20 bg-red-500/5 p-4" data-testid="error-patterns">
          <div className="flex items-center gap-2 mb-3">
            <FileWarning className="h-4 w-4 text-red-600" />
            <h3 className="text-sm font-semibold">Top parser error patterns</h3>
          </div>
          <div className="space-y-1.5">
            {topPatterns.map((p, i) => (
              <div key={i} className="flex items-center gap-3 text-sm">
                <span className="tabular-nums font-mono w-8 text-red-600">{p.count}×</span>
                <span className="text-muted-foreground">{p.pattern}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Parser analytics + quarantine bucket */}
      {analytics && analytics.total_broken > 0 && (
        <div className="rounded-xl border border-border bg-card p-4" data-testid="parser-analytics">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <PieChart className="h-4 w-4 text-purple-600" />
              <h3 className="text-sm font-semibold">Parser failure analytics</h3>
              <span className="text-xs text-muted-foreground">
                · {analytics.total_broken} broken / {analytics.total_documents} docs
                {' '}({analytics.failure_rate_pct}%)
              </span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowQuarantine((v) => !v)}
              data-testid="btn-toggle-quarantine"
            >
              <ShieldOff className="h-3.5 w-3.5" />
              <span className="ml-2 text-xs">
                {showQuarantine ? 'Hide' : 'Show'} quarantined ({quarantined.length})
              </span>
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
            <div>
              <div className="uppercase text-muted-foreground tracking-wider mb-1.5">By category</div>
              <div className="space-y-1">
                {(analytics.by_category || []).map((c) => (
                  <div
                    key={c.category}
                    className="flex items-center justify-between gap-3"
                    data-testid={'cat-' + c.category}
                  >
                    <div className="flex items-center gap-3">
                      <span className="tabular-nums font-mono w-8 text-purple-600">{c.count}×</span>
                      <span>{CATEGORY_LABELS[c.category] || c.category}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      {c.category === 'worker_restart' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-[10px]"
                          disabled={busy['bulk-retry-' + c.category]}
                          onClick={() => bulkRetry(c.category)}
                          data-testid={'bulk-retry-' + c.category}
                          title={'Retry all ' + c.count + ' ' + (CATEGORY_LABELS[c.category] || c.category) + ' docs'}
                        >
                          {busy['bulk-retry-' + c.category] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <PlayCircle className="h-3 w-3" />
                          )}
                          <span className="ml-0.5">Retry all</span>
                        </Button>
                      )}
                      {(c.category === 'corrupt_pdf' ||
                        c.category === 'password_protected' ||
                        c.category === 'scanned_no_ocr' ||
                        c.category === 'uncategorized') && c.count > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 px-1.5 text-[10px] text-purple-700"
                          disabled={busy['bulk-quar-' + c.category]}
                          onClick={() => bulkQuarantine(c.category, c.count)}
                          data-testid={'bulk-quar-' + c.category}
                          title={'Quarantine all ' + c.count + ' ' + (CATEGORY_LABELS[c.category] || c.category) + ' docs'}
                        >
                          {busy['bulk-quar-' + c.category] ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <ShieldOff className="h-3 w-3" />
                          )}
                          <span className="ml-0.5">Quarantine all</span>
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="uppercase text-muted-foreground tracking-wider mb-1.5">By vendor</div>
              <div className="space-y-1">
                {(analytics.by_vendor || []).slice(0, 6).map((v) => (
                  <div key={v.vendor} className="flex items-center gap-3">
                    <span className="tabular-nums font-mono w-8 text-purple-600">{v.count}×</span>
                    <span className="capitalize">{v.vendor || 'unknown'}</span>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <div className="uppercase text-muted-foreground tracking-wider mb-1.5">By file type</div>
              <div className="space-y-1">
                {(analytics.by_extension || []).slice(0, 6).map((e) => (
                  <div key={e.ext} className="flex items-center gap-3">
                    <span className="tabular-nums font-mono w-8 text-purple-600">{e.count}×</span>
                    <span className="uppercase">.{e.ext}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {analytics.ocr_candidates > 0 && (
            <div className="mt-3 text-xs text-amber-700 bg-amber-500/5 border border-amber-500/30 rounded-md px-3 py-2">
              {analytics.ocr_candidates} document(s) appear to be scanned/image-only — would benefit from OCR.
            </div>
          )}
        </div>
      )}

      {/* Quarantine drawer */}
      {showQuarantine && (
        <div className="rounded-xl border border-purple-500/20 bg-purple-500/5 p-4" data-testid="quarantine-drawer">
          <div className="flex items-center gap-2 mb-3">
            <ShieldOff className="h-4 w-4 text-purple-600" />
            <h3 className="text-sm font-semibold">Quarantined documents</h3>
            <span className="text-xs text-muted-foreground">
              Chunks removed from FAISS · blob retained for audit
            </span>
          </div>
          {quarantined.length === 0 ? (
            <div className="text-xs text-muted-foreground py-4 text-center">
              Nothing in quarantine. Use the shield icon on a failed row to add one.
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-muted-foreground uppercase tracking-wider">
                  <th className="py-1.5 pr-3 font-medium">Filename</th>
                  <th className="py-1.5 pr-3 font-medium">Vendor</th>
                  <th className="py-1.5 pr-3 font-medium">Category</th>
                  <th className="py-1.5 pr-3 font-medium">Reason</th>
                  <th className="py-1.5 pr-3 font-medium">By</th>
                  <th className="py-1.5 font-medium"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-purple-500/10">
                {quarantined.map((q) => (
                  <tr key={q.id} data-testid={'quar-row-' + q.id}>
                    <td className="py-2 pr-3 max-w-[260px] truncate" title={q.filename}>{q.filename || '—'}</td>
                    <td className="py-2 pr-3 capitalize">{q.vendor || '—'}</td>
                    <td className="py-2 pr-3">
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] border border-purple-500/30 text-purple-700 bg-purple-500/10">
                        {CATEGORY_LABELS[q.category] || q.category}
                      </span>
                    </td>
                    <td className="py-2 pr-3 max-w-[260px] truncate text-muted-foreground" title={q.reason}>
                      {q.reason || <em>—</em>}
                    </td>
                    <td className="py-2 pr-3 text-muted-foreground">{q.quarantined_by || '—'}</td>
                    <td className="py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        disabled={busy['unquar-' + q.id]}
                        onClick={() => unquarantineDoc(q.id)}
                        data-testid={'unquar-btn-' + q.id}
                      >
                        {busy['unquar-' + q.id] ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <>
                            <ShieldCheck className="h-3.5 w-3.5" />
                            <span className="ml-1">Restore</span>
                          </>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Filter + job log */}
      <div className="rounded-xl border border-border bg-card">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show:</span>
            {['failed', 'pending', 'completed', 'all'].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                className={
                  'px-3 py-1 rounded-md text-xs font-medium border transition-colors ' +
                  (filter === s
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-border text-muted-foreground hover:bg-secondary')
                }
                data-testid={'filter-' + s}
              >
                {s}
              </button>
            ))}
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchAll}
            disabled={loading}
            data-testid="btn-refresh-jobs"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
          </Button>
        </div>
        <ScrollArea className="h-[420px]">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-card border-b border-border">
              <tr className="text-left text-xs text-muted-foreground uppercase tracking-wider">
                <th className="px-4 py-2 font-medium">Filename</th>
                <th className="px-4 py-2 font-medium">Doc</th>
                <th className="px-4 py-2 font-medium">Job</th>
                <th className="px-4 py-2 font-medium">Vendor</th>
                <th className="px-4 py-2 font-medium">Chunks</th>
                <th className="px-4 py-2 font-medium">Error</th>
                <th className="px-4 py-2 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    {loading ? 'Loading…' : 'No jobs matching this filter.'}
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.job_id + '-' + r.doc_id} className="hover:bg-muted/30" data-testid={'job-row-' + r.doc_id}>
                    <td className="px-4 py-2 max-w-[260px] truncate" title={r.filename}>
                      {r.filename || <em className="text-muted-foreground">—</em>}
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge value={r.doc_status} />
                    </td>
                    <td className="px-4 py-2">
                      <StatusBadge value={r.job_status} />
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">
                      {r.vendor || '—'}
                      {r.domain ? ' / ' + r.domain : ''}
                    </td>
                    <td className="px-4 py-2 tabular-nums text-xs">{r.chunk_count != null ? r.chunk_count : '—'}</td>
                    <td className="px-4 py-2 text-xs max-w-[280px] truncate text-red-600" title={r.doc_error || r.job_error || ''}>
                      {r.doc_error || r.job_error || ''}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {(r.doc_status === 'error' || r.job_status === 'failed') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy['retry-' + r.doc_id]}
                            onClick={() => retryDoc(r.doc_id)}
                            data-testid={'retry-btn-' + r.doc_id}
                          >
                            {busy['retry-' + r.doc_id] ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <>
                                <PlayCircle className="h-3.5 w-3.5" />
                                <span className="ml-1 text-xs">Retry</span>
                              </>
                            )}
                          </Button>
                        )}
                        {(r.doc_status === 'error' || r.job_status === 'failed') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={busy['quar-' + r.doc_id]}
                            onClick={() => quarantineDoc(r.doc_id, r.filename)}
                            data-testid={'quarantine-btn-' + r.doc_id}
                            title="Quarantine this document (deletes chunks, keeps blob)"
                          >
                            {busy['quar-' + r.doc_id] ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            ) : (
                              <>
                                <ShieldOff className="h-3.5 w-3.5 text-purple-600" />
                                <span className="ml-1 text-xs text-purple-700">Quarantine</span>
                              </>
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </ScrollArea>
      </div>

      <p className="text-xs text-muted-foreground flex items-center gap-2">
        <Wrench className="h-3.5 w-3.5" />
        Mongo = canonical truth. FAISS rebuilds automatically on boot and every 24 h.
        Run <em>Integrity check</em> if you suspect drift; <em>Rebuild index</em> forces a refresh
        from Mongo; <em>Backfill metadata</em> repairs empty vendor/domain on bulk-ingested docs.
      </p>
      <CheckCircle2 className="hidden" />
    </div>
  );
}
